/**
 * Agent Chat History Component
 * Displays conversation history for a specific agent with delete functionality
 */

import { useState, useEffect } from 'react';
import { Trash2, MessageSquare, Calendar, X } from 'lucide-react';
import { useStorage } from '@/context/StorageContext';
import type { Agent } from '@/types/agent.types';
import type { Conversation } from '@/types/chat.types';
import { Button, LoadingSpinner } from '@/components/Common';
import { reportError } from '@/services/errorTelemetry';

interface AgentChatHistoryProps {
  agent: Agent;
  onClose: () => void;
}

export function AgentChatHistory({ agent, onClose }: AgentChatHistoryProps) {
  const { adapter } = useStorage();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.id]);

  const loadConversations = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const convs = await adapter.loadConversationsByAgent(agent.id);
      setConversations(convs);
    } catch (err) {
      console.error('Failed to load agent conversations:', err);
      setError('Failed to load chat history');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (conversationId: string) => {
    const conversation = conversations.find((c) => c.id === conversationId);
    if (!conversation) return;

    const confirmMessage =
      `Delete conversation "${conversation.title}"? ` +
      `This action cannot be undone.`;

    if (!confirm(confirmMessage)) return;

    try {
      setDeletingId(conversationId);
      await adapter.deleteConversation(conversationId);
      setConversations((prev) => prev.filter((c) => c.id !== conversationId));
    } catch (err) {
      reportError(err, { area: 'agent-chat-history', operation: 'delete-conversation', conversationId }, 'error');
      console.error('Failed to delete conversation:', err);
      alert('Failed to delete conversation. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return new Date(date).toLocaleDateString();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        className="relative w-full max-w-2xl max-h-[80vh] overflow-hidden rounded-lg bg-bg-primary shadow-xl"
        role="dialog"
        aria-labelledby="history-title"
        aria-describedby="history-description"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border-default px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{agent.icon || '🤖'}</span>
            <div>
              <h2
                id="history-title"
                className="text-xl font-semibold text-text-primary"
              >
                {agent.name} - Chat History
              </h2>
              <p id="history-description" className="text-sm text-text-secondary">
                {conversations.length} conversation
                {conversations.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={
              'rounded p-1 hover:bg-bg-secondary ' +
              'focus-visible:outline-none focus-visible:ring-2 ' +
              'focus-visible:ring-stellar-primary'
            }
            aria-label="Close chat history"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-6 py-4" style={{ maxHeight: 'calc(80vh - 140px)' }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" label="Loading chat history..." />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="text-4xl mb-4">❌</div>
              <p className="text-error">{error}</p>
              <Button onClick={loadConversations} className="mt-4" variant="secondary">
                Retry
              </Button>
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <MessageSquare className="h-12 w-12 text-text-tertiary mb-4" />
              <h3 className="text-lg font-medium text-text-primary">
                No conversations yet
              </h3>
              <p className="mt-2 text-sm text-text-secondary">
                Start a chat with {agent.name} to see conversation history here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className={
                    'rounded-lg border border-border-default bg-bg-primary p-4 ' +
                    'transition-all hover:shadow-md ' +
                    'focus-within:ring-2 focus-within:ring-stellar-primary ' +
                    'focus-within:ring-offset-2'
                  }
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-text-primary truncate">
                        {conversation.title}
                      </h4>
                      <div className="mt-1 flex items-center gap-4 text-sm text-text-secondary">
                        <div className="flex items-center gap-1">
                          <MessageSquare className="h-4 w-4" />
                          <span>{conversation.messageCount} messages</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDate(conversation.updatedAt)}</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDelete(conversation.id)}
                      disabled={deletingId === conversation.id}
                      className={
                        'rounded p-2 text-error transition-colors ' +
                        'hover:bg-error-light ' +
                        'focus-visible:outline-none focus-visible:ring-2 ' +
                        'focus-visible:ring-red-600 focus-visible:ring-offset-2 ' +
                        'disabled:opacity-50 disabled:cursor-not-allowed'
                      }
                      aria-label={`Delete conversation: ${conversation.title}`}
                    >
                      {deletingId === conversation.id ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <Trash2 className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border-default px-6 py-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-text-secondary">
              {conversations.length === 0
                ? 'No conversations to display'
                : `Showing all conversations with ${agent.name}`}
            </p>
            <Button onClick={onClose} variant="secondary">
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
